import React, { FC, Fragment } from "react";
import { formatAddress, dayjs, cn } from "utils";
import { EmptyState } from "@components/Empty";
import { PaymentHistoryProps, PayChannelMap } from "./types";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/CurrentPlan/ManageBillingModal/PaymentHistory";
import { useIsPc } from "@hooks/useIsPc";
import { PaymentRecord } from "@features/api/memberApi";
const I18n = I18nUtil.createScoped(Translations);

type PaymentItemType = PaymentRecord & {
  product_id?: string;
  productName?: string;
};

/**
 * 付款历史组件，显示历史记录表格
 * 使用 usePaymentHistory 钩子实现无限滚动加载
 */
const PaymentHistory: FC<PaymentHistoryProps> = ({
  paymentHistory,
  className,
}) => {
  const [isPc] = useIsPc();
  if (!paymentHistory || paymentHistory.length === 0) {
    return (
      <div className="flex flex-col gap-4 bg-background_solid_secondary w-full z-30 pt-6">
        {/* 固定表头 */}
        <div className="bg-background_solid_secondary w-full z-10">
          <EmptyState
            title={I18n.t("No Data Yet")}
            description={I18n.t("Payment History No Data")}
          />
        </div>
      </div>
    );
  }
  if (!isPc) {
    return (
      <div
        className={cn(
          "flex flex-col bg-background_solid_secondary w-full z-30 pt-6",
          className,
        )}
      >
        {paymentHistory.map((item: PaymentItemType, index: number) => (
          <div
            key={index}
            className="flex justify-between flex-col w-full py-2 border-b border-border_white_transparent10 last:border-b-0 last:pb-0 first:pt-0 group-loading:gap-1"
          >
            <div className="flex items-center flex-row justify-between pt-[6px] group-loading:skeleton group-loading:bg-background_solid_neutral_tertiary group-loading:rounded-1 group-loading:h-4.5">
              {!!item ? (
                <Fragment>
                  <span className="text-left text-content_primary body_medium_m">
                    {item?.productName}
                  </span>
                  <span className="text-left text-content_primary body_medium_m">
                    {item?.amount ?? 0} {item?.currency?.currency || "USD"}
                  </span>
                </Fragment>
              ) : null}
            </div>
            <div className="flex items-center flex-row justify-between pb-[6px] group-loading:skeleton group-loading:bg-background_solid_neutral_tertiary group-loading:rounded-1 group-loading:h-3.5">
              {!!item ? (
                <Fragment>
                  <span className="text-right text-content_secondary body_s">
                    {dayjs(item.payment_time).format("MMM DD,YYYY HH:mm:ss")}
                  </span>
                  <span className="pt-1 text-right text-content_tertiary body_s">
                    {item.channel === "WALLET"
                      ? formatAddress(item?.payer, 4)
                      : PayChannelMap[
                          item.channel as keyof typeof PayChannelMap
                        ]}
                  </span>
                </Fragment>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "self-stretch flex flex-col justify-start items-start relative",
        className,
      )}
    >
      {/* 标题部分 - 始终显示 */}
      <div className="flex flex-col gap-4 sticky top-0 bg-background_solid_secondary w-full z-30 pt-6">
        {/* 固定表头 */}
        <div className="bg-background_solid_secondary w-full z-10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border_white_transparent5 *:!text-content_tertiary *:!body_medium_s *:!font-normal">
                <th className="py-1 px-2 text-left w-24 min-w-24">
                  {I18n.t("Plan")}
                </th>
                <th className="py-1 px-2 text-left w-24 min-w-24">
                  {I18n.t("Amount")}
                </th>
                <th className="py-1 px-2 text-left w-24 min-w-24">
                  {I18n.t("Payment Method")}
                </th>
                <th className="py-1 px-2 text-left w-32 min-w-32">
                  {I18n.t("Date")}
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
      <table className="w-full">
        <tbody>
          {paymentHistory.map((item: PaymentItemType, index: number) => (
            <tr
              key={index}
              className="h-13 border-b border-border_white_transparent5"
            >
              <td className="py-[6px] px-2 text-left text-content_secondary body_medium_m w-24 min-w-24">
                {item?.productName}
              </td>
              <td className="py-[6px] px-2 text-left text-content_secondary body_medium_m w-24 min-w-24">
                {item.amount} {item.currency?.currency}
              </td>
              <td className="py-[6px] px-2 text-left text-content_secondary body_medium_m w-24 min-w-24">
                {item.channel === "WALLET"
                  ? formatAddress(item?.payer, 4)
                  : PayChannelMap[item.channel as keyof typeof PayChannelMap]}
              </td>
              <td className="py-[6px] px-2 text-left text-content_secondary body_medium_m w-32 min-w-32">
                {dayjs(item.payment_time).format("MMM DD,YYYY")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PaymentHistory;
